import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose } from 'react-icons/md';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  isDangerous?: boolean;
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonClass,
  isDangerous = false,
  isLoading = false,
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
          >
            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg rounded-2xl shadow-2xl"
              style={{
                background: 'linear-gradient(to bottom, #2a2a2a, #1f1f1f)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute cursor-pointer top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <MdClose size={24} className="text-gray-400 hover:text-white" />
              </button>


              <div className="p-8">

                <h2 className="text-xl font-medium text-brand-primary mb-4 pr-8">
                  {title}
                </h2>
                <p className="text-gray-400 text-xs leading-relaxed mb-8">
                  {message}
                </p>

                <div className="flex gap-4">
                  {/* Cancel Button */}
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2 rounded-xl font-normal cursor-pointer text-gray-900 bg-white hover:bg-gray-100 transition-colors"
                  >
                    {cancelText}
                  </button>

                  {/* Confirm Button */}
                  <button
                    onClick={handleConfirm}
                    disabled={isLoading}
                    className={
                      confirmButtonClass ||
                      `flex-1 px-4 py-2 rounded-xl font-normal text-white transition-colors cursor-pointer flex items-center justify-center gap-2 ${
                        isLoading
                          ? 'bg-gray-600 cursor-not-allowed'
                          : isDangerous
                          ? 'bg-brand-primary hover:bg-brand-primary cursor-pointer'
                          : 'bg-brand-primary hover:bg-[#d63d20] cursor-pointer'
                      }`
                    }
                  >
                    {isLoading && (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isLoading ? 'Deleting...' : confirmText}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfirmDialog;
